import { FALLBACK_DOMAIN } from '@constants/config';
import { getApiBaseUrl } from '../config';

export interface BlogData {
    id: string;
    title: string;
    date: string;
    image: string;
    summary: string;
    url: string;
}

interface FetchContext {
    companyId?: string;
    domain?: string;
}

export interface ApiRequestConfig {
    apiEndpoint: string;
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body?: Record<string, any>;
    params?: Record<string, any>;
}

const REQUEST_DEDUPE_WINDOW_MS = 1000;
const MAX_DEDUPE_ENTRIES = 200;
const requestResults = new Map<string, { expiresAt: number; result: Promise<any> }>();

export function fetchRawDataForRenderRequest(
    apiRequest: string | ApiRequestConfig,
    context?: FetchContext
): Promise<any> {
    const key = JSON.stringify([apiRequest, context?.companyId, context?.domain]);
    const now = Date.now();
    const cached = requestResults.get(key);
    if (cached && cached.expiresAt > now) return cached.result;

    for (const [entryKey, entry] of requestResults) {
        if (entry.expiresAt <= now) requestResults.delete(entryKey);
    }

    const result = DataFetcher.fetchRawData(apiRequest, context);
    requestResults.set(key, { expiresAt: now + REQUEST_DEDUPE_WINDOW_MS, result });

    if (requestResults.size > MAX_DEDUPE_ENTRIES) {
        const oldestKey = requestResults.keys().next().value;
        if (oldestKey !== undefined) requestResults.delete(oldestKey);
    }

    return result;
}

export class DataFetcher {
    private static resolveDomain(domain?: string): string {
        const normalizedDomain = typeof domain === 'string' ? domain.trim() : '';
        return normalizedDomain || FALLBACK_DOMAIN;
    }

    private static extractCollection(value: any): any[] {
        if (Array.isArray(value)) {
            return value;
        }

        if (!value || typeof value !== 'object') {
            return [];
        }

        const collected: any[] = [];

        Object.values(value).forEach((nestedValue) => {
            const nestedCollection = this.extractCollection(nestedValue);
            if (nestedCollection.length > 0) {
                collected.push(...nestedCollection);
            }
        });

        return collected;
    }

    static normalizeCollectionResponse(data: any): any[] {
        if (Array.isArray(data)) {
            return data;
        }

        if (!data || typeof data !== 'object') {
            return [];
        }

        if (Array.isArray(data.data)) {
            return data.data.some((entry: any) => Array.isArray(entry))
                ? data.data.flat()
                : data.data;
        }

        if (data.data && typeof data.data === 'object') {
            const nestedData = this.extractCollection(data.data);
            if (nestedData.length > 0) {
                return nestedData;
            }
        }

        return this.extractCollection(data);
    }

    private static resolveApiEndpoint(apiEndpoint: string): string {
        if (!apiEndpoint) {
            return apiEndpoint;
        }

        const normalized = apiEndpoint.trim();
        if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
            return normalized;
        }

        const baseUrl = getApiBaseUrl().replace(/\/$/, '');
        const path = normalized.startsWith('/') ? normalized : `/${normalized}`;
        return `${baseUrl}${path}`;
    }

    /**
     * Fetch blog data from API using POST
     */
    static async fetchBlogData(apiRequest: string | ApiRequestConfig, context?: FetchContext): Promise<BlogData[]> {
        try {
            const data = await this.fetchRawData(apiRequest, context);

            // Transform the data based on API response structure
            const blogArray = this.normalizeCollectionResponse(data);

            return blogArray.map((blog: any) => ({
                id: blog.id || '',
                title: blog.title || '',
                date: blog.date || blog.created_at || '',
                image: blog.image || blog.featured_image || '',
                summary: blog.summary || blog.content || '',
                url: blog.url || blog.slug || '',
                // Alias fields for template placeholders
                blog_title: blog.title || '',
                blog_date: blog.date || blog.created_at || '',
                blog_image: blog.image || blog.featured_image || '',
                blog_summary: blog.summary || blog.content || '',
                read_more_url: blog.link || blog.url || blog.slug || '',
            }));
        } catch (error) {
            console.error(`Error fetching blog data from ${typeof apiRequest === 'string' ? apiRequest : apiRequest.apiEndpoint}:`, error);
            return [];
        }
    }

    static async fetchRawData(apiRequest: string | ApiRequestConfig, context?: FetchContext): Promise<any> {
        const requestConfig: ApiRequestConfig = typeof apiRequest === 'string'
            ? { apiEndpoint: apiRequest, method: 'POST', body: {} }
            : apiRequest;

        const resolvedEndpoint = this.resolveApiEndpoint(requestConfig.apiEndpoint);
        const payload: Record<string, any> = {
            ...(requestConfig.body ?? {}),
            ...(requestConfig.params ?? {}),
        };

        if (context?.companyId) {
            payload.company_id = context.companyId;
        }

        if (context?.domain) {
            payload.domain = context.domain;
        }

        if ('domain' in payload) {
            payload.domain = this.resolveDomain(payload.domain);
        } else if (context && 'domain' in context) {
            payload.domain = this.resolveDomain(context.domain);
        }

        const method = requestConfig.method.toUpperCase() as 'GET' | 'POST' | 'PUT' | 'DELETE';

        try {
            if (method === 'GET') {
                const getUrl = new URL(resolvedEndpoint);
                Object.entries(payload).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        getUrl.searchParams.set(key, String(value));
                    }
                });

                const getResponse = await fetch(getUrl.toString(), {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                if (!getResponse.ok) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn(`[DataFetcher] GET failed: ${getUrl.toString()} - ${getResponse.status} ${getResponse.statusText}`);
                    }
                    return null;
                }

                const json = await getResponse.json();
                console.log('[DataFetcher] GET response:', getUrl.toString());
                console.dir(json, { depth: null });
                if (json && typeof json === 'object' && json.status === false) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('[DataFetcher] GET returned status false:', json);
                    }
                    return null;
                }

                return json;
            }

            const response = await fetch(resolvedEndpoint, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                if (process.env.NODE_ENV === 'development') {
                    console.warn(`[DataFetcher] ${method} failed: ${resolvedEndpoint} - ${response.status} ${response.statusText}`);
                }
                return null;
            }

            const json = await response.json();
            console.log('[DataFetcher] API response:', method, resolvedEndpoint);
            console.dir(json, { depth: null });
            if (json && typeof json === 'object' && json.status === false) {
                if (process.env.NODE_ENV === 'development') {
                    console.warn('[DataFetcher] POST returned status false:', json);
                }
                return null;
            }

            return json;
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('[DataFetcher] Request error:', error);
            }
            return null;
        }
    }

    /**
     * Fetch generic API data with optional transformation
     */
    static async fetchData<T = any>(
        apiEndpoint: string,
        context?: FetchContext,
        transformer?: (data: any) => T[]
    ): Promise<T[]> {
        try {
            const data = await this.fetchRawData(apiEndpoint, context);

            if (transformer) {
                return transformer(data);
            }
            return this.normalizeCollectionResponse(data);

        } catch (error) {
            console.error(`Error fetching data from ${apiEndpoint}:`, error);
            return [];
        }
    }

}
